import { FC } from 'react';
import { IonIcon } from '@ionic/react';
import { downloadOutline, logOutOutline } from 'ionicons/icons';
import Button from '../../../components/Button/Button';
import useAnimatedDots from '../../../hooks/useAnimatedDots';
import { useTranslate } from '../../../hooks/context/useTranslate';
import { ExportProgressState } from '../types';
import resolveProgressPercent from '../utils/resolveProgressPercent';

interface DatasetExportHeaderProps {
  isExporting: boolean;
  isDisabled: boolean;
  progress: ExportProgressState | null;
  onBack: () => void;
  onDownload: () => void;
  onCancel: () => void;
}

const DatasetExportHeader: FC<DatasetExportHeaderProps> = ({
  isExporting,
  isDisabled,
  progress,
  onBack,
  onDownload,
  onCancel,
}) => {
  const { t } = useTranslate();
  const progressPercent = resolveProgressPercent(progress);
  const exportingLabel =
    progress?.phase === 'generating'
      ? t('export.preparing')
      : t('export.fetching');
  const showDotsIndicator =
    isExporting &&
    progress?.phase === 'fetching' &&
    (progress?.total ?? 0) <= 1;
  const animatedDots = useAnimatedDots(showDotsIndicator);

  return (
    <div className="header dataset-export-header">
      <h1>{t('export.dataTitle')}</h1>
      {/* Fullscreen export follows the same inline action layout as form/reference pages. */}
      <div className="dataset-export-header-actions">
        <Button onClick={onBack} fill="clear" color="dark">
          <IonIcon icon={logOutOutline} slot="start" />
          {t('export.leave')}
        </Button>
        {isExporting && (
          <Button onClick={onCancel} fill="outline" color="danger">
            {t('export.cancel')}
          </Button>
        )}
        <Button
          className="dataset-export-save-button"
          onClick={onDownload}
          fill="solid"
          round={false}
          disabled={isDisabled}
        >
          <IonIcon icon={downloadOutline} slot="start" />
          {!isExporting && t('export.saveToDisk')}
          {isExporting && showDotsIndicator && (
            <>
              {exportingLabel}
              <span className="dataset-export-fetching-dots">
                {animatedDots}
              </span>
            </>
          )}
          {isExporting && !showDotsIndicator && (
            <>
              {exportingLabel}
              <span className="dataset-export-progress-percent">
                &nbsp;{progressPercent}%
              </span>
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default DatasetExportHeader;
